import React, { useCallback, useRef, useState } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';

// Promise-based confirmation dialog hook. Replaces every native
// `window.confirm()` in the app so we get:
//   • Consistent branded look (shadcn AlertDialog styling).
//   • No browser "Prevent this page from creating additional
//     dialogs" checkbox to suppress future dialogs.
//   • Support for rich content (title + description + optional
//     destructive variant with rose CTA).
//
// Usage:
//   const { confirm, dialog } = useConfirmDialog();
//   // Somewhere in the render:  {dialog}
//   // In a handler:
//   const ok = await confirm({
//     title: 'Delete this prayer?',
//     description: 'This cannot be undone.',
//     confirmText: 'Delete',
//     destructive: true,
//   });
//   if (!ok) return;
//   // …proceed…
//
// Returns:
//   `confirm(options)`  — opens the dialog, resolves to true on
//                         confirm-click or false on cancel/close.
//   `dialog`            — JSX to embed in the caller's render.
//
// Implementation detail: we keep the resolver in a ref so the dialog
// button handlers can call it even after the calling component re-
// renders. The caller does NOT need to memoise `dialog` — its
// identity is stable enough between renders that React's Radix
// portal doesn't unmount.
export function useConfirmDialog() {
  const [state, setState] = useState({
    open: false,
    title: '',
    description: '',
    confirmText: 'Confirm',
    cancelText: 'Cancel',
    destructive: false,
    preview: null, // Optional { label?: string, items: string[] }
  });
  const resolverRef = useRef(null);

  const confirm = useCallback((options = {}) => {
    return new Promise((resolve) => {
      resolverRef.current = resolve;
      setState({
        open: true,
        title: options.title || 'Are you sure?',
        description: options.description || '',
        confirmText: options.confirmText || 'Confirm',
        cancelText: options.cancelText || 'Cancel',
        destructive: Boolean(options.destructive),
        preview: options.preview || null,
      });
    });
  }, []);

  const settle = (result) => {
    // Close first, then resolve on the next tick so the underlying
    // dialog's exit transition can start before the caller runs its
    // post-confirm logic (which is often a re-render that would
    // otherwise fight the transition).
    setState((prev) => ({ ...prev, open: false }));
    const resolver = resolverRef.current;
    resolverRef.current = null;
    if (resolver) resolver(result);
  };

  const dialog = (
    <AlertDialog
      open={state.open}
      onOpenChange={(open) => {
        // Radix invokes onOpenChange(false) when the user clicks
        // outside, presses Escape, or clicks Cancel. Any of those
        // count as a "no" answer.
        if (!open) settle(false);
      }}
    >
      <AlertDialogContent data-testid="confirm-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle data-testid="confirm-dialog-title">
            {state.title}
          </AlertDialogTitle>
          {state.description && (
            <AlertDialogDescription data-testid="confirm-dialog-description">
              {state.description}
            </AlertDialogDescription>
          )}
        </AlertDialogHeader>
        {state.preview && Array.isArray(state.preview.items) && state.preview.items.length > 0 && (
          // Preview panel — tiny read-only list of the items the user is
          // about to act on. Especially useful for destructive bulk
          // actions so a mis-click on the wrong row is caught here.
          <div
            data-testid="confirm-dialog-preview"
            className="rounded-lg border border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900/60 px-3 py-2.5 text-sm"
          >
            {state.preview.label && (
              <div className="text-[11px] uppercase tracking-wide font-semibold text-zinc-500 mb-1.5">
                {state.preview.label}
              </div>
            )}
            <ul className="space-y-1 max-h-40 overflow-y-auto pr-1">
              {state.preview.items.slice(0, 8).map((title, i) => (
                <li
                  // Preview list is a static one-shot render — items never
                  // reorder or insert mid-lifecycle — so an index-tie key
                  // is safe, but we compose it with the title so React's
                  // reconciler notices when the same slot changes content
                  // (e.g. two confirms in a row with different payloads).
                  key={`${i}-${(title || 'untitled').slice(0, 32)}`}
                  data-testid={`confirm-preview-item-${i}`}
                  className="flex items-start gap-2 text-zinc-800 dark:text-zinc-200"
                >
                  <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-amber-400 dark:bg-amber-500 flex-shrink-0" aria-hidden="true" />
                  <span className="line-clamp-2 leading-snug">{title || <span className="italic text-zinc-400">Untitled prayer</span>}</span>
                </li>
              ))}
              {state.preview.items.length > 8 && (
                <li
                  data-testid="confirm-preview-overflow"
                  className="text-xs italic text-zinc-500 pl-3.5"
                >
                  …and {state.preview.items.length - 8} more
                </li>
              )}
            </ul>
          </div>
        )}
        <AlertDialogFooter>
          <AlertDialogCancel
            onClick={() => settle(false)}
            data-testid="confirm-dialog-cancel"
          >
            {state.cancelText}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={() => settle(true)}
            data-testid="confirm-dialog-confirm"
            className={
              state.destructive
                ? 'bg-rose-600 hover:bg-rose-700 text-white focus:ring-rose-400'
                : undefined
            }
          >
            {state.confirmText}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );

  return { confirm, dialog };
}
